import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyPayment } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const ref = params.get("reference") || params.get("trxref") || params.get("m_payment_id");
  const cancelled = params.get("cancelled") === "1";
  const [state, setState] = useState(cancelled ? "failed" : "verifying");

  useEffect(() => {
    if (cancelled || !ref) { setState(cancelled ? "failed" : "failed"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const d = await verifyPayment(ref);
        if (d.status === "success") { setState("success"); return; }
        if (d.status === "failed") { setState("failed"); return; }
        // pending — Paystack webhook may still be arriving; keep polling briefly.
        if (tries++ < 8) { timer = setTimeout(poll, 2000); }
        else { setState("pending"); }
      } catch {
        if (tries++ < 8) { timer = setTimeout(poll, 2000); } else { setState("failed"); }
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [ref, cancelled]);

  return (
    <AuthShell>
      <div className="text-center" data-testid="payment-callback">
        {state === "verifying" && <><Loader2 className="w-12 h-12 animate-spin text-[#D4AF37] mx-auto" /><h2 className="font-display text-2xl font-bold text-[#0B132B] mt-5">Confirming your payment…</h2><p className="text-slate-500 text-sm mt-2">This is verified securely on our server via Paystack. Please wait.</p></>}
        {state === "success" && <><CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" /><h2 className="font-display text-2xl font-bold text-[#0B132B] mt-5">Payment successful!</h2><p className="text-slate-500 text-sm mt-2">Your WiseAce workspace is now active.</p><Button onClick={() => nav("/billing")} className="mt-6 bg-[#0B132B] hover:bg-[#1C2541] h-11 px-8" data-testid="callback-continue">Go to Billing</Button></>}
        {state === "pending" && <><Loader2 className="w-12 h-12 animate-spin text-[#D4AF37] mx-auto" /><h2 className="font-display text-2xl font-bold text-[#0B132B] mt-5">Almost there…</h2><p className="text-slate-500 text-sm mt-2">We're still confirming your payment with Paystack. This can take a moment.</p><Button onClick={() => nav("/billing")} className="mt-6 bg-[#0B132B] hover:bg-[#1C2541] h-11 px-8" data-testid="callback-pending">Go to Billing</Button></>}
        {state === "failed" && <><XCircle className="w-14 h-14 text-rose-500 mx-auto" /><h2 className="font-display text-2xl font-bold text-[#0B132B] mt-5">{cancelled ? "Payment cancelled" : "Payment was unsuccessful"}</h2><p className="text-slate-500 text-sm mt-2">No charge was completed. You can try again.</p><Button onClick={() => nav("/billing")} className="mt-6 bg-[#0B132B] hover:bg-[#1C2541] h-11 px-8" data-testid="callback-retry">Try again</Button></>}
      </div>
    </AuthShell>
  );
}
